import Anthropic from '@anthropic-ai/sdk';
import type { QuizPackWithKey } from './quiz-pack';
import { resolveBridgeUrl } from '../../shared/bridge-resolver';

export interface ScanGradeResult {
  correctCount: number;
  totalCount: number;
  perQuestion: { idx: number; kidAnswer: string; correct: boolean; expected: string; }[];
}

/** Read lazily so tests (and Vite) can inject VITE_WORKER_URL at call time. */
function workerUrl(): string | undefined {
  const v = import.meta.env.VITE_WORKER_URL as string | undefined;
  return v ? v : undefined;
}

/**
 * Bridge availability is pre-checked by resolveBridgeUrl (fast /bridge/url
 * lookup); this timeout covers the actual Claude vision grading (claude -p,
 * ~10-40s). 2.5s aborted every real call → grading never used the bridge.
 */
const BRIDGE_TIMEOUT_MS = 60_000;

/**
 * Grade a scanned, hand-written practice paper.
 *
 * Primary path: POST the image + pack to the Worker `/grade` proxy so the Claude
 * key stays on the backend. Falls back to the legacy browser-side
 * `@anthropic-ai/sdk` call (VITE_ANTHROPIC_API_KEY) when VITE_WORKER_URL is unset
 * or the worker is unavailable (5xx/503). Signature and return type are unchanged.
 */
export async function gradeScannedPaper(file: File, pack: QuizPackWithKey): Promise<ScanGradeResult> {
  const base64 = await fileToBase64(file);
  const mediaType = (file.type || 'image/jpeg') as 'image/jpeg' | 'image/png' | 'image/webp';

  // Provider order: local Claude bridge (first) → worker (next) → browser key (last).
  const bridge = await resolveBridgeUrl();
  if (bridge) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BRIDGE_TIMEOUT_MS);
    try {
      const res = await fetch(`${bridge}/grade`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mediaType, pack }),
        signal: controller.signal,
      });
      if (res.ok) {
        return (await res.json()) as ScanGradeResult;
      }
      // Bridge reachable but failed → fall through to worker/browser fallback.
    } catch {
      // Bridge unreachable/timed out (not running) → fall through to worker/browser.
    } finally {
      clearTimeout(timer);
    }
  }

  const url = workerUrl();
  if (url) {
    try {
      const res = await fetch(`${url}/grade`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mediaType, pack }),
      });
      if (res.ok) {
        return (await res.json()) as ScanGradeResult;
      }
      // 4xx (client error, not 503) → real failure, surface it.
      // 5xx / 503 (AI not configured) → graceful fallback to browser path.
      if (res.status < 500) {
        throw new Error(`grade failed: ${res.status}`);
      }
    } catch (e) {
      // 4xx: surface. Network error or 5xx: fall through to browser fallback.
      if (e instanceof Error && e.message.startsWith('grade failed:')) throw e;
    }
  }

  return gradeViaBrowser(base64, mediaType, pack);
}

/** Legacy browser-side path: dev-only, uses VITE_ANTHROPIC_API_KEY directly. */
async function gradeViaBrowser(
  base64: string,
  mediaType: 'image/jpeg' | 'image/png' | 'image/webp',
  pack: QuizPackWithKey,
): Promise<ScanGradeResult> {
  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
  if (!apiKey) throw new Error('VITE_ANTHROPIC_API_KEY missing');
  const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true });

  // Build a prompt that gives the model the question prompts so it knows WHAT to look for
  const promptText = `這是一張九歲小孩手寫的練習卷掃描照片(卷號 ${pack.packId})。請逐題判讀小孩寫的答案,只輸出 JSON 陣列,每題一個物件:{ "idx": 題號 (1-N), "kidAnswer": "小孩寫的答案 (盡力辨識)" }

題目列表:
${pack.questions.map((q, i) => `${i + 1}. ${q.prompt}${q.kind === 'mcq' ? ` 選項: ${(q.options as (string | number)[]).map((o, idx) => `(${String.fromCharCode(65 + idx)}) ${o}`).join(' ')}` : ' (填空)'}`).join('\n')}

對 MCQ:輸出 "A" / "B" / "C" / "D" (小孩圈選的字母),如果沒圈或圈兩個都無法判讀就輸出 "?"
對填空題:盡量讀出小孩寫的數字或文字,寬鬆判讀(歪斜、潦草都試試)

只輸出 JSON 陣列,沒有 markdown,沒有解釋。`;

  const resp = await client.messages.create({
    model: 'claude-opus-4-5',
    max_tokens: 2048,
    messages: [{
      role: 'user',
      content: [
        { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
        { type: 'text', text: promptText },
      ],
    }],
  });

  const text = resp.content.filter(b => b.type === 'text').map(b => (b as { text: string }).text).join('');
  const cleaned = text.replace(/^```(?:json)?\n?|\n?```$/g, '').trim();
  const kidAnswers = JSON.parse(cleaned) as { idx: number; kidAnswer: string }[];

  return buildResult(kidAnswers, pack);
}

/** Compare extracted kid answers against the answer key. Shared shape with worker. */
function buildResult(
  kidAnswers: { idx: number; kidAnswer: string }[],
  pack: QuizPackWithKey,
): ScanGradeResult {
  const perQuestion = pack.answerKey.map(entry => {
    const found = kidAnswers.find(a => a.idx === entry.idx);
    const kidAnswer = found?.kidAnswer ?? '';
    const correct = compareLenient(kidAnswer, entry.correct);
    return { idx: entry.idx, kidAnswer, correct, expected: entry.correct };
  });
  const correctCount = perQuestion.filter(p => p.correct).length;
  return { correctCount, totalCount: pack.answerKey.length, perQuestion };
}

export function compareLenient(kid: string, expected: string): boolean {
  if (!kid) return false;
  const k = kid.trim().toUpperCase();
  const e = expected.trim().toUpperCase();
  if (k === e) return true;
  // Numeric comparison: if both parse as numbers
  const kn = parseFloat(kid);
  const en = parseFloat(expected);
  if (!isNaN(kn) && !isNaN(en)) return kn === en;
  // Handle "X 餘 Y" fill answers: compare normalized whitespace
  const kNorm = k.replace(/\s+/g, ' ');
  const eNorm = e.replace(/\s+/g, ' ');
  if (kNorm === eNorm) return true;
  return false;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
